import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { Btn, Card, Input, Select, PageTitle, StatusChip, DemoBadge, Empty, Tag, cx } from "../components/kit";
import { Loading } from "../components/common";
import { useAccess, AccessRequired } from "../components/Access";
import { api } from "../lib/api";
import { usePaged, useDebounced, LoadMore } from "../lib/paged";
import { useAuth } from "../lib/auth";
import { fmtDate, fmtDateTime, fmtLocal, todayISO } from "../lib/format";

const DEFAULTS = { tab: "dogs", q: "", status: "", direction: "" };

export default function Practice() {
  const { t } = useTranslation();
  const { me, setMe } = useAuth();
  const [f, setF] = useState({ ...DEFAULTS, ...(me.user.prefs?.practice || {}) });
  const first = useRef(true);
  const q = useDebounced(f.q);
  const pg = usePaged(f.tab === "dogs" ? "/dogs" : "/programmes", { q: q || undefined, status: f.status || undefined, direction: f.direction || undefined, closed: f.tab === "closed" || undefined, training: f.training ? "include" : undefined });
  const rows = pg.items;
  const acc = useAccess();
  const locked = acc && !acc.practice;

  useEffect(() => {
    if (!first.current) api.put("/auth/profile", { prefs: { practice: f } }).then((r) => setMe(r.data)).catch(() => {});
    first.current = false;
  }, [f.tab, q, f.status, f.direction, f.training]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const statuses = f.tab === "dogs" ? ["none", "draft", "active", "completed", "discontinued"] : f.tab === "closed" ? ["completed", "discontinued"] : ["draft", "active", "completed", "discontinued"];
  return (
    <div>
      <PageTitle title={t("practice.title")} sub={t("practice.sub")} right={locked ? <Btn testid="new-dog-btn" disabled><Plus className="h-4 w-4" />{t("practice.new_dog")}</Btn> : <Link to="/dogs/new"><Btn testid="new-dog-btn"><Plus className="h-4 w-4" />{t("practice.new_dog")}</Btn></Link>} />
      {locked && <div className="mb-4"><AccessRequired area="practice" testid="practice-access-required" /></div>}
      <div role="tablist" className="mb-4 flex flex-wrap gap-2">
        {["dogs", "programmes", "closed"].map((k) => (
          <button key={k} role="tab" aria-selected={f.tab === k} data-testid={`practice-tab-${k}`} onClick={() => setF({ ...f, tab: k, status: "" })}
            className={cx("min-h-[44px] rounded-xl border px-4 text-sm font-semibold", f.tab === k ? "bg-white border-primary" : "bg-transparent text-muted-foreground hover:bg-white")}>{t(`practice.tab_${k}`)}</button>
        ))}
      </div>
      <Card className="mb-5 grid gap-3 p-4 sm:grid-cols-[1fr_200px_200px]">
        <label className="relative block">
          <span className="sr-only">{t("practice.search")}</span>
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="practice-search-input" className="pl-9" placeholder={t("practice.search_ph")} value={f.q} onChange={set("q")} />
        </label>
        <Select data-testid="practice-status-filter" aria-label={t("practice.status")} value={f.status} onChange={set("status")}>
          <option value="">{t("practice.all_statuses")}</option>
          {statuses.map((s) => <option key={s} value={s}>{s === "none" ? t("practice.no_programme") : t(`status.${s}`)}</option>)}
        </Select>
        <Select data-testid="practice-direction-filter" aria-label={t("practice.direction")} value={f.direction} onChange={set("direction")}>
          <option value="">{t("practice.all_directions")}</option>
          {["A", "B", "C", "D"].map((d) => <option key={d} value={d}>{d} — {t(`dir.${d}`)}</option>)}
        </Select>
      </Card>
      {!rows ? <Loading /> : (
        <>
          <label className="mb-2 flex min-h-[44px] items-center gap-2 text-sm"><input type="checkbox" data-testid="practice-training-toggle" checked={!!f.training} onChange={(e) => setF({ ...f, training: e.target.checked })} />{t("l.show_training")}</label>
          <p data-testid="practice-count" className="mb-3 text-sm font-semibold text-muted-foreground">{t("practice.count", { count: pg.total })}</p>
          {rows.length === 0 ? <Empty testid="practice-empty" title={t("practice.empty")} action={<Link to="/dogs/new"><Btn variant="soft">{t("practice.new_dog")}</Btn></Link>} /> : (
            <ul className="grid gap-3">
              {f.tab === "dogs" ? rows.map((d) => <DogRow key={d.id} d={d} tz={me.user.time_zone} />) : rows.map((p) => <ProgRow key={p.id} p={p} />)}
            </ul>
          )}
          <LoadMore shown={rows.length} total={pg.total} onMore={pg.more} loading={pg.loading} testid="practice" />
        </>
      )}
    </div>
  );
}

function DogRow({ d, tz }) {
  const { t } = useTranslation();
  const overdue = d.next_visit && d.next_visit.scheduled_local.slice(0, 10) < todayISO();
  return (
    <li>
      <Link to={`/dogs/${d.id}`} data-testid={`dog-row-${d.number}`} className="lift block rounded-2xl border bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          {d.is_demo ? <img src={`/img/b9/dog_${(parseInt(String(d.number).replace(/\D/g, ""), 10) % 3) + 1 || 1}.webp`} alt={t("b9.example_photo")} loading="lazy" className="h-11 w-11 rounded-full border object-cover" data-testid={`dog-avatar-${d.number}`} />
            : <span className="grid h-11 w-11 place-items-center rounded-full bg-peach font-display font-semibold text-clay-strong" aria-hidden data-testid={`dog-avatar-${d.number}`}>{(d.name || "?")[0]}</span>}
          <span className="font-display text-lg font-bold">{d.name}</span>
          <Tag>{d.number}</Tag>
          {d.is_demo && <DemoBadge short />}{d.is_training && <Tag data-testid={`training-tag-${d.number}`}>{t("l.training")}</Tag>}
          <span className="ml-auto">{d.programme_status ? <StatusChip status={d.programme_status} /> : <StatusChip status="no_response" label={t("practice.no_programme")} />}</span>
        </div>
        <div className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-4">
          <span>{t("common.responsible")}: <b className="text-foreground">{d.responsible_name}</b></span>
          <span>{t("practice.latest")}: {fmtDateTime(d.latest_activity, tz)}</span>
          <span>{t("practice.next")}: {overdue ? <b className="text-rose-ink">{t("common.overdue_since", { date: fmtDate(d.next_visit.scheduled_local.slice(0, 10)) })}</b> : d.next_visit ? fmtLocal(d.next_visit.scheduled_local, d.next_visit.time_zone) : d.next_step || "—"}</span>
          <span>{t("practice.counts", { programmes: d.programme_count, reviews: d.open_reviews })}</span>
        </div>
        {d.directions.length > 0 && <div className="mt-2 flex gap-1">{d.directions.map((x) => <Tag key={x}>{x}</Tag>)}</div>}
      </Link>
    </li>
  );
}

function ProgRow({ p }) {
  const { t } = useTranslation();
  return (
    <li>
      <Link to={`/programmes/${p.id}`} data-testid={`programme-row-${p.number}`} className="lift block rounded-2xl border bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-display text-lg font-bold">{p.title || p.number}</span><Tag>{p.number}</Tag>{p.is_demo && <DemoBadge short />}
          {p.archived && <StatusChip status="archived" label={t("status.archived")} />}
          <span className="ml-auto"><StatusChip status={p.status} /></span>
        </div>
        <div className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-4">
          <span>{p.dog_name}</span><span>{t("common.responsible")}: <b className="text-foreground">{p.responsible_name}</b></span>
          <span>{t("programme.review_date")}: {p.review_date ? fmtDate(p.review_date) : "—"}</span>
          <span>{p.directions.join(" · ")} · {t("practice.stages", { count: p.stage_count })}</span>
        </div>
      </Link>
    </li>
  );
}
